import { useMemo, useState } from 'react';
import { ChevronDown, Download } from 'lucide-react';
import { useOpsStore } from '../../store/ops.store';
import type { HeatmapCell } from '../../types/ops';

function getCheckinColor(ratio: number): string {
  // Green/blue (low) -> Yellow (mid) -> Red/Orange (high)
  if (ratio <= 0.5) {
    const t = ratio / 0.5;
    const r = Math.round(34 + t * (234 - 34));
    const g = Math.round(197 + t * (179 - 197));
    const b = Math.round(94 + t * (8 - 94));
    return `rgb(${r}, ${g}, ${b})`;
  }
  const t = (ratio - 0.5) / 0.5;
  const r = Math.round(234 + t * (220 - 234));
  const g = Math.round(179 - t * 179);
  const b = Math.round(8);
  return `rgb(${r}, ${g}, ${b})`;
}

function buildGrid(data: HeatmapCell[]) {
  const studioSet = new Set<string>();
  const timeslotSet = new Set<string>();
  let maxValue = 0;

  for (const cell of data) {
    studioSet.add(cell.studioName);
    timeslotSet.add(cell.timeslot);
    if (cell.value > maxValue) maxValue = cell.value;
  }

  const studios = Array.from(studioSet).sort();
  const timeslots = Array.from(timeslotSet).sort();

  const lookup = new Map<string, number>();
  for (const cell of data) {
    lookup.set(`${cell.studioName}|${cell.timeslot}`, cell.value);
  }

  return { studios, timeslots, lookup, maxValue };
}

export default function CheckinHeatmap({ isDark }: { isDark: boolean }) {
  const checkinHeatmap = useOpsStore((s) => s.checkinHeatmap);

  const programs = useMemo(() => {
    const set = new Set<string>();
    for (const cell of checkinHeatmap) set.add(cell.programName);
    return Array.from(set).sort();
  }, [checkinHeatmap]);

  const [selectedProgram, setSelectedProgram] = useState<string>('');

  // Auto-select first program when data changes
  const activeProgram = selectedProgram && programs.includes(selectedProgram)
    ? selectedProgram
    : programs[0] ?? '';

  const filteredData = useMemo(
    () => checkinHeatmap.filter((c) => c.programName === activeProgram),
    [checkinHeatmap, activeProgram],
  );

  const { studios, timeslots, lookup, maxValue } = useMemo(
    () => buildGrid(filteredData),
    [filteredData],
  );

  if (checkinHeatmap.length === 0) {
    return (
      <div className={`rounded-lg p-6 text-center ${isDark ? 'bg-slate-800 text-slate-400' : 'bg-white text-gray-500 border border-gray-200 shadow-sm'}`}>
        Chưa có dữ liệu heatmap check-in.
      </div>
    );
  }

  return (
    <div className={`rounded-lg p-3 sm:p-4 ${isDark ? 'bg-slate-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-3 sm:mb-4">
        <h3 className={`text-base sm:text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Heatmap Check-in</h3>
        <div className="relative">
          <select
            value={activeProgram}
            onChange={(e) => setSelectedProgram(e.target.value)}
            className={`appearance-none pl-3 pr-8 py-1.5 rounded-lg border text-sm font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-500/50 ${
              isDark
                ? 'bg-slate-900 border-slate-600 text-white'
                : 'bg-gray-50 border-gray-300 text-gray-900'
            }`}
          >
            {programs.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <ChevronDown className={`absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
        </div>
        <div className="flex-1" />
        <button
          onClick={() => {
            const header = ['Studio', ...timeslots];
            const csvRows = [header.join(',')];
            for (const studio of studios) {
              csvRows.push([
                `"${studio.replace(/"/g, '""')}"`,
                ...timeslots.map(ts => lookup.get(`${studio}|${ts}`) ?? 0),
              ].join(','));
            }
            const blob = new Blob(['\uFEFF' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `checkin_heatmap_${activeProgram.replace(/\s+/g, '_')}.csv`;
            a.click();
            URL.revokeObjectURL(url);
          }}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            isDark
              ? 'bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
          }`}
        >
          <Download className="w-3.5 h-3.5" />
          Export CSV
        </button>
      </div>
      <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
        <div
          className="inline-grid gap-px"
          style={{
            gridTemplateColumns: `280px repeat(${timeslots.length}, minmax(40px, 1fr))`,
          }}
        >
          {/* Header row */}
          <div className={`sticky left-0 z-10 ${isDark ? 'bg-slate-800' : 'bg-white'}`} />
          {timeslots.map((ts) => (
            <div
              key={ts}
              className={`px-2 py-1 text-center text-xs font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}
            >
              {ts}
            </div>
          ))}

          {/* Data rows */}
          {studios.map((studio) => (
            <>
              <div
                key={`label-${studio}`}
                className={`sticky left-0 z-10 overflow-hidden pr-3 text-sm font-medium ${isDark ? 'bg-slate-800 text-slate-200' : 'bg-white text-gray-700'}`}
                title={studio}
              >
                <span className="block truncate leading-9">{studio}</span>
              </div>
              {timeslots.map((ts) => {
                const value = lookup.get(`${studio}|${ts}`) ?? 0;
                const ratio = maxValue > 0 ? value / maxValue : 0;
                return (
                  <div
                    key={`${studio}|${ts}`}
                    className="flex h-9 items-center justify-center rounded text-xs font-semibold text-white"
                    style={{
                      backgroundColor: value > 0 ? getCheckinColor(ratio) : (isDark ? 'rgb(30, 41, 59)' : 'rgb(229, 231, 235)'),
                    }}
                    title={`${studio} @ ${ts}: ${value}`}
                  >
                    {value > 0 ? value : ''}
                  </div>
                );
              })}
            </>
          ))}
        </div>
      </div>
    </div>
  );
}
